export const MAX_IMPORT_ROWS = 500;

export type ImportRow = { serial: string; pin: string };
export type ImportColumn = keyof ImportRow;
export type ImportResult = { serial: string; pin: string; keys: string; errors?: string[] };

const COLUMNS: ImportColumn[] = ["serial", "pin"];

const isHeaderRow = (cells: string[]) =>
  cells.length >= 2 && cells[0].trim().toLowerCase() === "serial" && cells[1].trim().toLowerCase() === "pin";

const splitLine = (line: string): string[] => {
  if (line.includes("\t")) {
    return line.split("\t");
  }
  if (line.includes(",")) {
    return line.split(",");
  }
  return line.trim().split(/\s+/);
};

// Spreadsheets copy cells as tab separated text; a single value (no tab) fills just the first column.
export const parsePastedText = (text: string): string[][] => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");
  const rows = lines.map((line) => splitLine(line).map((cell) => cell.trim().replace(/^"(.*)"$/, "$1")));
  return rows.length && isHeaderRow(rows[0]) ? rows.slice(1) : rows;
};

export const emptyRow = (): ImportRow => ({ serial: "", pin: "" });

const isEmptyRow = (row: ImportRow) => !row.serial.trim() && !row.pin.trim();

// Pastes the parsed cells into the grid starting at the focused cell, adding rows as needed.
export const applyPaste = (
  rows: ImportRow[],
  startRow: number,
  startColumn: ImportColumn,
  pasted: string[][]
): ImportRow[] => {
  const next = rows.map((row) => ({ ...row }));
  const firstColumn = COLUMNS.indexOf(startColumn);
  pasted.forEach((cells, rowOffset) => {
    const rowIndex = startRow + rowOffset;
    while (next.length <= rowIndex) {
      next.push(emptyRow());
    }
    cells.forEach((cell, cellOffset) => {
      const column = COLUMNS[firstColumn + cellOffset];
      if (column) {
        next[rowIndex][column] = cell;
      }
    });
  });
  return next;
};

// Adds pasted or loaded rows by serial: a serial already in the grid has its pin updated in place,
// so loading the same device twice never creates a second row. Rows land in empty rows first.
// A missing or blank pin never overwrites a pin that is already there.
export const mergeRows = (rows: ImportRow[], incoming: string[][]): ImportRow[] => {
  const next = rows.map((row) => ({ ...row }));
  const bySerial = new Map<string, ImportRow>();
  next.forEach((row) => {
    const key = normalizeSerial(row.serial);
    if (key && !bySerial.has(key)) {
      bySerial.set(key, row);
    }
  });
  let emptyIndex = 0;
  const takeEmptyRow = (): ImportRow => {
    while (emptyIndex < next.length && !isEmptyRow(next[emptyIndex])) {
      emptyIndex++;
    }
    if (emptyIndex === next.length) {
      next.push(emptyRow());
    }
    return next[emptyIndex];
  };
  incoming.forEach(([serial = "", pin = ""]) => {
    const key = normalizeSerial(serial);
    if (!key) {
      return;
    }
    let target = bySerial.get(key);
    if (!target) {
      target = takeEmptyRow();
      target.serial = serial.trim();
      bySerial.set(key, target);
    }
    if (pin.trim()) {
      target.pin = pin.trim();
    }
  });
  return next;
};

// Same normalisation the server applies (trim, upper case serial), so statuses can be matched back to rows.
export const normalizeSerial = (serial: string) => serial.trim().toUpperCase();

export const getRowsToImport = (rows: ImportRow[]): ImportRow[] =>
  rows
    .filter((row) => !isEmptyRow(row))
    .map((row) => ({ serial: normalizeSerial(row.serial), pin: row.pin.trim() }));

// Returns one message per problem, keyed by the row's index in the grid.
export const validateRows = (rows: ImportRow[]): { rowErrors: Record<number, string>; formError?: string } => {
  const rowErrors: Record<number, string> = {};
  const seen = new Set<string>();
  let count = 0;
  rows.forEach((row, index) => {
    if (isEmptyRow(row)) {
      return;
    }
    count++;
    const serial = normalizeSerial(row.serial);
    if (!serial || !row.pin.trim()) {
      rowErrors[index] = "Serial and pin are both required";
    } else if (seen.has(serial)) {
      rowErrors[index] = "Duplicate serial";
    }
    seen.add(serial);
  });
  let formError: string | undefined;
  if (count === 0) {
    formError = "Add at least one device";
  } else if (count > MAX_IMPORT_ROWS) {
    formError = `At most ${MAX_IMPORT_ROWS} devices can be imported at once`;
  }
  return { rowErrors, formError };
};

export const resultsBySerial = (results: ImportResult[] = []): Record<string, ImportResult> =>
  results.reduce((acc, result) => ({ ...acc, [normalizeSerial(result.serial)]: result }), {});

export const summarizeResults = (results: ImportResult[] = []) => {
  const imported = results.filter((r) => r.pin === "created" && r.keys === "created").length;
  const failed = results.filter((r) => r.pin === "error" || r.keys === "error").length;
  return { imported, failed };
};
